import { animated, useSprings } from "@react-spring/web";
import { useEffect, useMemo } from "react";

import bert from "../assets/sesame-street-characters/bert.webp";
import bigbird from "../assets/sesame-street-characters/bigbird.png";
import cookiemonster from "../assets/sesame-street-characters/cookiemonster.png";
import elmo from "../assets/sesame-street-characters/elmo.png";
import ernie from "../assets/sesame-street-characters/ernie.png";
import kermit from "../assets/sesame-street-characters/kermit.webp";
import oscar from "../assets/sesame-street-characters/oscar.png";
import rosita from "../assets/sesame-street-characters/rosita.png";

const CHARACTERS = [elmo, bigbird, cookiemonster, bert, ernie, oscar, rosita, kermit];
const STAGGER_MS = 600;
const WALK_MS = 7000;
const TOTAL_MS = (CHARACTERS.length - 1) * STAGGER_MS + WALK_MS + 300;

const rand = (min: number, max: number) => Math.random() * (max - min) + min;

interface SesameStreetParadeProps {
  onComplete: () => void;
}

export const SesameStreetParade: React.FC<SesameStreetParadeProps> = ({ onComplete }) => {
  const characterData = useMemo(
    () =>
      CHARACTERS.map(() => ({
        bounceHeight: rand(45, 70),
        tension: rand(320, 480),
        friction: rand(5, 9),
      })),
    [],
  );

  // Horizontal march — linear, staggered
  const walkSprings = useSprings(
    CHARACTERS.length,
    CHARACTERS.map((_, i) => ({
      from: { x: -160 },
      to: { x: window.innerWidth + 160 },
      delay: i * STAGGER_MS,
      config: { duration: WALK_MS, easing: (t: number) => t },
    })),
  );

  // Vertical bounce + squash & stretch — looping, each character slightly different
  const bounceSprings = useSprings(
    CHARACTERS.length,
    characterData.map((d, i) => ({
      from: { y: 0, rotate: -7, scaleX: 1.06, scaleY: 0.9 },
      to: { y: -d.bounceHeight, rotate: 7, scaleX: 0.94, scaleY: 1.1 },
      loop: { reverse: true },
      delay: i * STAGGER_MS,
      config: { tension: d.tension, friction: d.friction },
    })),
  );

  useEffect(() => {
    const t = setTimeout(onComplete, TOTAL_MS);
    return () => clearTimeout(t);
  }, [onComplete]);

  return (
    <div style={{ position: "fixed", inset: 0, pointerEvents: "none", zIndex: 9999, overflow: "hidden" }}>
      {CHARACTERS.map((src, i) => (
        <animated.div key={i} style={{ position: "absolute", bottom: 30, left: 0, ...walkSprings[i] }}>
          <animated.img
            src={src}
            alt=""
            style={{ width: 128, height: 128, objectFit: "contain", display: "block", ...bounceSprings[i] }}
          />
        </animated.div>
      ))}
    </div>
  );
};
